import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { DB, Auth } from '~/services/firebase';
import { useAuth } from '~/hooks/auth';

import AuthTemplate from '~/layouts/Auth';
import getValidationErrors from '~/utils/getValidationErrors';

interface SigInFormData {
  email: string;
  password: string;
}

interface UserData {
  name: string;
  email: string;
  uid: string;
  isAdmin?: boolean;
}

const SignIn: React.FC = () => {
  const { t } = useTranslation();
  const { updateUser } = useAuth();

  const [data, setData] = useState({
    email: '',
    password: '',
  } as SigInFormData);
  const [errors, setErrors] = useState({} as any);
  const [passwordType, setPasswordType] = useState('password');
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();
      setErrors({});

      try {
        const schema = Yup.object().shape({
          email: Yup.string()
            .required(t('index.field_required', { field: t('index.email') }))
            .email(t('index.field_invalid', { field: t('index.email') })),
          password: Yup.string()
            .required(t('index.field_required', { field: t('index.password') }))
            .min(
              8,
              t('index.field_min', { field: t('index.password'), number: 8 }),
            ),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const { email, password } = data;

        setLoading(true);

        const q = query(collection(DB, 'users'), where('email', '==', email));

        const response = await getDocs(q);

        if (response.docs.length > 0) {
          const { name, uid, isAdmin } = response.docs[0].data() as UserData;

          const responseAuth = await signInWithEmailAndPassword(
            Auth,
            email,
            password,
          );

          if (responseAuth.user.uid)
            updateUser({
              uid,
              name,
              email,
              avatar: null,
              isAdmin,
            });
        } else {
          setErrors({
            message:
              'E-mail ou Senha incorretos. Por favor, verique e tente novamente.',
          });
        }

        setLoading(false);
      } catch (err: any) {
        console.log(err);
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
        }
      }
    },
    [data, t, updateUser],
  );

  return (
    <AuthTemplate>
      <div className="nk-block-head">
        <div className="nk-block-head-content">
          <h5 className="nk-block-title">Fatia Prime Pizzaria</h5>
          <div className="nk-block-des">
            <p>Mais vendas, economia e eficiência em seu Delivery.</p>
          </div>
        </div>
      </div>

      {errors.message && (
        <div className="example-alert mb-3">
          <div className="alert alert-danger alert-icon alert-dismissible">
            <em className="icon ni ni-cross-circle" />
            {errors.message}
            <Link to="#close" className="close" data-dismiss="alert" />
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <div className="form-label-group">
            <label className="form-label" htmlFor="default-01">
              {t('index.email')}
            </label>
          </div>
          <div className="form-control-wrap">
            <input
              type="text"
              className="form-control form-control-lg"
              placeholder={t('index.email_placeholder')}
              onChange={event => {
                setData({
                  ...data,
                  email: event.target.value,
                });
              }}
            />
            {errors.email && <span className="invalid">{errors.email}</span>}
          </div>
        </div>
        <div className="form-group">
          <div className="form-label-group">
            <label className="form-label" htmlFor="password">
              {t('index.password')}
            </label>
            <Link to="/recovery" className="link link-primary link-sm">
              {t('index.forgot_password')}
            </Link>
          </div>
          <div className="form-control-wrap">
            <Link
              to="#eye"
              className="form-icon form-icon-right passcode-switch lg"
              data-target="password"
              onClick={() => {
                setPasswordType(
                  passwordType === 'password' ? 'text' : 'password',
                );
              }}
            >
              {passwordType === 'password' && (
                <em className="passcode-icon icon-show icon ni ni-eye" />
              )}
              {passwordType === 'text' && (
                <em className="passcode-icon icon-show icon ni ni-eye-off" />
              )}
            </Link>
            <input
              type={passwordType}
              className="form-control form-control-lg"
              placeholder={t('index.password_placeholder')}
              onChange={event => {
                setData({
                  ...data,
                  password: event.target.value,
                });
              }}
            />
            {errors.password && (
              <span className="invalid">{errors.password}</span>
            )}
          </div>
        </div>

        <div className="form-group">
          <button
            type="submit"
            className="btn btn-lg btn-primary btn-block"
            disabled={loading}
          >
            {loading && (
              <div className="spinner-border spinner-border-sm" role="status" />
            )}
            {!loading && t('index.signin')}
          </button>
        </div>
      </form>
      <div className="form-note-s2 pt-4">
        {t('index.new_on_platform')}{' '}
        <Link to="/sign-up">{t('index.create_account')}</Link>
      </div>
    </AuthTemplate>
  );
};

export default SignIn;
